import { PaperPlaneTilt } from '@phosphor-icons/react';
import type { FormationDuServeur, LangueParlee, MembreDeLAnnuaire } from '@sirocco/shared';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { messages as messagesApi, users as usersApi } from '../../api/endpoints';
import { Avatar } from '../../components/ui/Avatar';
import { BadgeEmoji, BadgeOfficiel } from '../../components/ui/Bits';
import { Button, IconButton } from '../../components/ui/Button';
import { useAsync } from '../../hooks/useAsync';
import { useSession } from '../../store/SessionContext';
import { drapeauxDe, ficheDe } from '../../utils/langues';
import { profilePath } from '../../utils/routes';
import { t } from '../../i18n';

/**
 * Ce que partagent l'accueil de l'annuaire et la liste d'une formation.
 *
 * Les deux ecrans lisent la meme reponse, sous la meme cle : ouvrir une
 * formation depuis son bloc l'affiche tout de suite, sans relire.
 */

/** La formation de ceux qui n'en ont pas dit : son bloc, et son adresse. */
export const SANS_FORMATION = 'aucune';

export function useAnnuaire() {
  const { server } = useSession();
  return useAsync(() => usersApi.annuaire(), [server?.id], `annuaire-${server?.id ?? ''}`);
}

/** « Élodie » et « elodie » se cherchent l'un l'autre. */
export function sansAccents(texte: string): string {
  return texte.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/* Le pseudo et la bio : « BDE » ou « Lyon » trouvent ceux qui en parlent. */
function texteDe(membre: MembreDeLAnnuaire): string {
  return [membre.user.username, membre.user.bio].join(' ');
}

/** Ce que la fenetre de filtres regle. Un ensemble vide ne retire rien. */
export interface FiltresAnnuaire {
  formations: Set<string>;
  langues: Set<LangueParlee>;
}

export const SANS_FILTRE = (): FiltresAnnuaire => ({
  formations: new Set(),
  langues: new Set(),
});

export function filtreActif(filtres: FiltresAnnuaire): boolean {
  return Object.values(filtres).some((ensemble: Set<unknown>) => ensemble.size > 0);
}

/**
 * Les membres qui passent la recherche et les filtres.
 *
 * Dans un meme groupe, il suffit d'une case : « PGE » et « BBA » gardent
 * les deux formations. D'un groupe a l'autre, il les faut toutes : « PGE »
 * et « Anglais » ne gardent que ceux du PGE qui parlent anglais. C'est
 * ainsi qu'on resserre la liste en ajoutant des criteres.
 */
export function filtrer(
  membres: MembreDeLAnnuaire[],
  recherche: string,
  filtres: FiltresAnnuaire = SANS_FILTRE(),
): MembreDeLAnnuaire[] {
  const cherche = sansAccents(recherche.trim());
  return membres.filter((membre) => {
    if (cherche && !sansAccents(texteDe(membre)).includes(cherche)) return false;
    if (filtres.formations.size && !filtres.formations.has(membre.formation ?? SANS_FORMATION)) {
      return false;
    }
    if (filtres.langues.size && !membre.langues.some((code) => filtres.langues.has(code))) {
      return false;
    }
    return true;
  });
}

/** Ajouter une valeur a un groupe, ou l'en retirer. */
function basculer<T>(ensemble: Set<T>, valeur: T): Set<T> {
  const suivant = new Set(ensemble);
  if (suivant.has(valeur)) suivant.delete(valeur);
  else suivant.add(valeur);
  return suivant;
}

/**
 * La fenetre des filtres de l'annuaire.
 *
 * Chaque toucher filtre tout de suite : la liste derriere suit, et le
 * bouton du bas ne fait que refermer. Les langues proposees sont celles
 * des membres : une case qui ne retiendrait personne serait un cul-de-sac.
 */
export function FiltresDeLAnnuaire({
  valeur,
  onChange,
  membres,
  formations,
  onFermer,
}: {
  valeur: FiltresAnnuaire;
  onChange: (suivant: FiltresAnnuaire) => void;
  membres: MembreDeLAnnuaire[];
  /** Absent : la fenetre ne propose pas de formation (on est deja dans une). */
  formations?: FormationDuServeur[];
  onFermer: () => void;
}) {
  const textes = t.messages.annuaire.filtres;
  const langues = [...new Set(membres.flatMap((membre) => membre.langues))].sort((a, b) =>
    ficheDe(a).nom.localeCompare(ficheDe(b).nom),
  );
  const sansFormation = membres.some((membre) => !membre.formation);

  function pastille<T>(cle: string, libelle: string, groupe: keyof FiltresAnnuaire, valeurDe: T) {
    const ensemble = valeur[groupe] as Set<T>;
    const on = ensemble.has(valeurDe);
    return (
      <button
        key={cle}
        type="button"
        className={`cv-pastille${on ? ' cv-pastille--on' : ''}`}
        aria-pressed={on}
        onClick={() => onChange({ ...valeur, [groupe]: basculer(ensemble, valeurDe) })}
      >
        {libelle}
      </button>
    );
  }

  return (
    <div className="an-filtres">
      <p className="ev-filter__head">{textes.titre}</p>

      {formations && formations.length > 0 && (
        <>
          <p className="ev-filter__label">{textes.formation}</p>
          <div className="cv-pastilles">
            {formations.map((formation) =>
              pastille(formation.id, formation.nom, 'formations', formation.id),
            )}
            {sansFormation &&
              pastille(SANS_FORMATION, t.messages.annuaire.sansFormation, 'formations', SANS_FORMATION)}
          </div>
        </>
      )}

      {langues.length > 0 && (
        <>
          <p className="ev-filter__label">{textes.langues}</p>
          <div className="cv-pastilles">
            {langues.map((code) =>
              pastille(code, `${ficheDe(code).drapeau} ${ficheDe(code).nom}`, 'langues', code),
            )}
          </div>
        </>
      )}

      <div className="ev-filter__actions">
        <Button variant="soft" size="md" onClick={() => onChange(SANS_FILTRE())}>
          {textes.effacer}
        </Button>
        <Button size="md" onClick={onFermer}>
          {textes.fermer}
        </Button>
      </div>
    </div>
  );
}

/**
 * Les membres sous la lettre de leur pseudo, comme dans le carnet
 * d'adresses du telephone.
 *
 * La liste arrive deja triee : on ne fait que la couper. Un pseudo qui ne
 * commence pas par une lettre latine se range sous « # », en tete.
 */
export function ListeParLettre({
  membres,
  onEchec,
}: {
  membres: MembreDeLAnnuaire[];
  onEchec: (message: string) => void;
}) {
  const groupes = new Map<string, MembreDeLAnnuaire[]>();
  for (const membre of membres) {
    const premiere = sansAccents(membre.user.username.charAt(0)).toUpperCase();
    const lettre = /^[A-Z]$/.test(premiere) ? premiere : '#';
    groupes.set(lettre, [...(groupes.get(lettre) ?? []), membre]);
  }
  const lettres = [...groupes].sort(([a], [b]) =>
    a === '#' ? -1 : b === '#' ? 1 : a.localeCompare(b),
  );
  return (
    <>
      {lettres.map(([lettre, liste]) => (
        <section key={lettre} className="an__lettre">
          <h2 className="an__initiale">{lettre}</h2>
          {liste.map((membre) => (
            <Membre key={membre.user.id} membre={membre} onEchec={onEchec} />
          ))}
        </section>
      ))}
    </>
  );
}

/**
 * Une ligne : le visage et le pseudo menent au profil, l'avion ecrit.
 *
 * Ecrire se fait sans passer par le profil : c'est la raison d'etre de
 * l'annuaire. A quelqu'un qui n'est pas un ami, le message part en
 * demande, comme depuis son profil.
 */
function Membre({
  membre,
  onEchec,
}: {
  membre: MembreDeLAnnuaire;
  onEchec: (message: string) => void;
}) {
  const { user: moi } = useSession();
  const navigate = useNavigate();
  const [ouvre, setOuvre] = useState(false);
  const { user } = membre;
  const drapeaux = drapeauxDe(membre.langues);

  async function ecrire() {
    setOuvre(true);
    try {
      const conversation = await messagesApi.openDirect(user.id);
      navigate(`/app/messages/${conversation.id}`);
    } catch (cause) {
      // Un refus se dit : sans cela, l'avion paraitrait mort.
      onEchec(cause instanceof Error ? cause.message : t.sorties.fiche.ecrireImpossible);
    } finally {
      setOuvre(false);
    }
  }

  return (
    <div className="card ms-person an-membre">
      <Link to={profilePath(user.id, moi?.id)} className="ms-person__open">
        <Avatar user={user} size={44} />
        <span className="ms-person__body">
          <span className="ms-person__name an-membre__nom">
            <span className="an-membre__pseudo">{user.username}</span>
            {user.verifie && <BadgeOfficiel size="sm" />}
            <BadgeEmoji user={user} />
          </span>
          {(drapeaux || user.bio.trim()) && (
            <span className="an-membre__detail">
              {drapeaux && <span className="drapeau an-membre__langues">{drapeaux}</span>}
              {user.bio.trim() && <span className="an-membre__bio">{user.bio.trim()}</span>}
            </span>
          )}
        </span>
      </Link>

      <IconButton
        label={t.messages.annuaire.ecrireA(user.username)}
        size={38}
        className="an-membre__ecrire"
        disabled={ouvre}
        onClick={() => void ecrire()}
      >
        <PaperPlaneTilt className="sens-x" size={19} />
      </IconButton>
    </div>
  );
}

/** Un message qui passe, puis s'efface : le temps de le lire. */
export function useToast(): [string | null, (message: string) => void] {
  const [toast, setToast] = useState<string | null>(null);
  function flash(message: string) {
    setToast(message);
    setTimeout(() => setToast(null), Math.max(2600, message.length * 45));
  }
  return [toast, flash];
}
